import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Pencil, Link as LinkIcon } from 'lucide-react';
import { Unit } from '../types/unit';
import { Video, ModelSpace } from '../types/model';
import { unitService } from '../services/unitService';
import { videoService } from '../services/videoService';
import { Note, getNotes, createNote, deleteNote } from '../services/noteService';
import api from '../services/api';
import SortableList from './SortableList';
import AddItemsModal from './AddItemsModal';
import RichTextEditor from './RichTextEditor';
import ConfirmDialog from './ConfirmDialog';
import PhotoGallery from './PhotoGallery';
import DocumentGallery from './DocumentGallery';
import VideoGallery, { VideoGalleryHandle } from './VideoGallery';
import ChangeHistoryButton from './ChangeHistoryButton';
import ChangeHistoryDrawer from './ChangeHistoryDrawer';
import '../styles/UnitDetailsSlider.css';

interface UnitDetailsSliderProps {
  workspaceId: string;
  unitId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const UnitDetailsSlider: React.FC<UnitDetailsSliderProps> = ({
  workspaceId,
  unitId,
  onClose,
  onSuccess
}) => {
  const [unit, setUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editModeUnitInfo, setEditModeUnitInfo] = useState(false);
  const [editModeFinance, setEditModeFinance] = useState(false);
  const [unitInfoFormData, setUnitInfoFormData] = useState<{
    status?: 'AVAILABLE' | 'RESERVED' | 'SOLD' | 'UNAVAILABLE';
    modelId?: string;
    floorMarketing?: number;
    floorLegal?: string;
    unitNoMarketing?: number;
    unitNoLegal?: string;
    bed?: number;
    bath?: number;
    den?: number;
    flex?: number;
    media?: number;
    ceiling?: number;
    area?: number;
    outdoorType?: 'Balcony' | 'Terrace' | 'Patio' | 'None';
    outdoorArea?: number;
    buildingType?: 'Condominium' | 'Townhouse' | 'Detached' | 'Semi-Detached';
    unitEnrolmentNo?: string;
  }>({});
  const [financeFormData, setFinanceFormData] = useState<{
    price?: number;
    unitPremium?: number;
    floorPremium?: number;
    tax?: number;
  }>({});

  // Views editing
  const [editingViews, setEditingViews] = useState(false);
  const [viewsData, setViewsData] = useState<string[]>([]);
  const [savingViews, setSavingViews] = useState(false);
  const [showAddViewsModal, setShowAddViewsModal] = useState(false);

  // Spaces editing
  const [editingSpaces, setEditingSpaces] = useState(false);
  const [spacesData, setSpacesData] = useState<string[]>([]);
  const [savingSpaces, setSavingSpaces] = useState(false);
  const [showAddSpacesModal, setShowAddSpacesModal] = useState(false);

  // Allocations editing
  const [editingAllocations, setEditingAllocations] = useState(false);
  const [allocationsData, setAllocationsData] = useState<{
    eligibleToBuy: {
      parking: boolean;
      maxParking: number;
      locker: boolean;
      maxLocker: number;
      bicycle: boolean;
      maxBicycle: number;
    };
  }>({
    eligibleToBuy: {
      parking: false,
      maxParking: 0,
      locker: false,
      maxLocker: 0,
      bicycle: false,
      maxBicycle: 0
    }
  });
  const [savingAllocations, setSavingAllocations] = useState(false);

  // Allocation Modals
  const [showParkingModal, setShowParkingModal] = useState(false);
  const [showLockerModal, setShowLockerModal] = useState(false);
  const [showBicycleModal, setShowBicycleModal] = useState(false);
  const [parkingAllocations, setParkingAllocations] = useState<Array<{ type: string; number: number; price: number }>>([]);
  const [lockerAllocations, setLockerAllocations] = useState<Array<{ type: string; number: number; price: number }>>([]);
  const [bicycleAllocations, setBicycleAllocations] = useState<Array<{ type: string; number: number; price: number }>>([]);
  const [loadingParkingTypes, setLoadingParkingTypes] = useState(false);
  const [loadingLockerTypes, setLoadingLockerTypes] = useState(false);
  const [loadingBicycleTypes, setLoadingBicycleTypes] = useState(false);
  const [savingAllocation, setSavingAllocation] = useState(false);

  // Notes management
  const [notes, setNotes] = useState<Note[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);

  // Video management
  const [videos, setVideos] = useState<Video[]>([]);
  const videoGalleryRef = React.useRef<VideoGalleryHandle>(null);

  // Model views and spaces (fetched from model)
  const [modelViews, setModelViews] = useState<string[]>([]);
  const [modelSpaces, setModelSpaces] = useState<string[]>([]);

  // Models list for dropdown
  const [models, setModels] = useState<Array<{ _id: string; name: string }>>([]);

  /* data bootstrap effect is placed below after callbacks are declared */

  useEffect(() => {
    // Disable body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when modal closes
      document.body.style.overflow = '';
    };
  }, []);

  const fetchUnit = React.useCallback(async () => {
    try {
      setLoading(true);
      const [unitResponse, videosData] = await Promise.all([
        unitService.getById(workspaceId, unitId),
        videoService.getVideos(workspaceId, 'unit', unitId)
      ]);
      setUnit(unitResponse.unit);
      setVideos(videosData || []);
      // Initialize form data
      setUnitInfoFormData({
        status: unitResponse.unit.status,
        modelId: unitResponse.unit.modelId,
        floorMarketing: unitResponse.unit.floorMarketing,
        floorLegal: unitResponse.unit.floorLegal,
        unitNoMarketing: unitResponse.unit.unitNoMarketing,
        unitNoLegal: unitResponse.unit.unitNoLegal,
        bed: unitResponse.unit.bed,
        bath: unitResponse.unit.bath,
        den: unitResponse.unit.den,
        flex: unitResponse.unit.flex,
        media: unitResponse.unit.media,
        ceiling: unitResponse.unit.ceiling,
        area: unitResponse.unit.area,
        outdoorType: unitResponse.unit.outdoorType,
        outdoorArea: unitResponse.unit.outdoorArea,
        buildingType: unitResponse.unit.buildingType,
        unitEnrolmentNo: unitResponse.unit.unitEnrolmentNo
      });
      setFinanceFormData({
        price: unitResponse.unit.price,
        unitPremium: unitResponse.unit.unitPremium,
        floorPremium: unitResponse.unit.floorPremium,
        tax: unitResponse.unit.tax
      });

      // Fetch model views and spaces
      if (unitResponse.unit.modelId) {
        fetchModelViewsAndSpaces(unitResponse.unit.modelId);
      }

      // Fetch models for the dropdown (if status is AVAILABLE)
      if (unitResponse.unit.projectId) {
        try {
          const modelsResponse = await api.get(`/workspaces/${workspaceId}/models`, {
            params: {
              projectId: unitResponse.unit.projectId,
              limit: 1000,
              sortBy: 'name',
              sortOrder: 'asc'
            }
          });
          setModels(modelsResponse.data.models || []);
        } catch (err) {
          console.error('Error fetching models:', err);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch unit details');
      console.error('Error fetching unit:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, unitId]);

  const fetchModelViewsAndSpaces = async (modelId: string) => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/models/${modelId}`);
      const model = response.data.model;
      setModelViews(model.views || []);
      // Extract space names from ModelSpace objects
      const spaceNames = (model.spaces || []).map((s: ModelSpace | string) =>
        typeof s === 'string' ? s : s.name
      );
      setModelSpaces(spaceNames);
    } catch (err: any) {
      console.error('Error fetching model views/spaces:', err);
    }
  };

  

  const handleUnitInfoEdit = () => {
    if (unit) {
      setUnitInfoFormData({
        status: unit.status,
        modelId: unit.modelId,
        floorMarketing: unit.floorMarketing,
        floorLegal: unit.floorLegal,
        unitNoMarketing: unit.unitNoMarketing,
        unitNoLegal: unit.unitNoLegal,
        bed: unit.bed,
        bath: unit.bath,
        den: unit.den,
        flex: unit.flex,
        media: unit.media,
        ceiling: unit.ceiling,
        area: unit.area,
        outdoorType: unit.outdoorType,
        outdoorArea: unit.outdoorArea,
        buildingType: unit.buildingType,
        unitEnrolmentNo: unit.unitEnrolmentNo
      });
    }
    setEditModeUnitInfo(true);
  };

  const handleFinanceEdit = () => {
    if (unit) {
      setFinanceFormData({
        price: unit.price,
        unitPremium: unit.unitPremium,
        floorPremium: unit.floorPremium,
        tax: unit.tax
      });
    }
    setEditModeFinance(true);
  };

  const handleUnitInfoSave = async () => {
    try {
      setSaving(true);
      await unitService.update(workspaceId, unitId, unitInfoFormData);
      await fetchUnit();
      setEditModeUnitInfo(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update unit information');
    } finally {
      setSaving(false);
    }
  };

  const handleFinanceSave = async () => {
    try {
      setSaving(true);
      await unitService.update(workspaceId, unitId, financeFormData);
      await fetchUnit();
      setEditModeFinance(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update finance information');
    } finally {
      setSaving(false);
    }
  };

  const handleUnitInfoCancel = () => {
    setEditModeUnitInfo(false);
  };

  const handleFinanceCancel = () => {
    setEditModeFinance(false);
  };

  const handleDelete = async () => {
    try {
      await unitService.delete(workspaceId, unitId);
      toast.success('Unit deleted successfully');
      if (onSuccess) {
        onSuccess(); // Trigger parent refresh
      }
      onClose(); // Close slider
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete unit');
    }
  };

  const fetchNotes = React.useCallback(async () => {
    try {
      setLoadingNotes(true);
      const fetchedNotes = await getNotes(workspaceId, 'unit', unitId);
      setNotes(fetchedNotes);
    } catch (err: any) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoadingNotes(false);
    }
  }, [workspaceId, unitId]);

  // Bootstrap data when unitId changes
  useEffect(() => {
    fetchUnit();
    fetchNotes();
  }, [fetchUnit, fetchNotes]);

  // Views handlers
  const handleEditViews = () => {
    if (!unit) return;
    setViewsData(unit.views || []);
    setEditingViews(true);
  };

  const handleCancelViews = () => {
    setEditingViews(false);
  };

  const handleViewsChange = (views: string[]) => {
    setViewsData(views);
  };

  const handleAddViewsFromModal = (newViews: string[]) => {
    setViewsData(prev => [...prev, ...newViews]);
  };

  const handleSaveViews = async () => {
    if (!unit) return;

    try {
      setSavingViews(true);
      const response = await unitService.update(workspaceId, unitId, {
        views: viewsData
      });

      setUnit(response.unit);
      setEditingViews(false);
    } catch (err: any) {
      console.error('Error updating views:', err);
      toast.error(err.response?.data?.message || 'Failed to update views');
    } finally {
      setSavingViews(false);
    }
  };

  // Spaces handlers
  const handleEditSpaces = () => {
    if (!unit) return;
    setSpacesData(unit.spaces || []);
    setEditingSpaces(true);
  };

  const handleCancelSpaces = () => {
    setEditingSpaces(false);
  };

  const handleSpacesChange = (spaces: string[]) => {
    setSpacesData(spaces);
  };

  const handleAddSpacesFromModal = (newSpaces: string[]) => {
    setSpacesData(prev => [...prev, ...newSpaces]);
  };

  const handleSaveSpaces = async () => {
    if (!unit) return;

    try {
      setSavingSpaces(true);
      const response = await unitService.update(workspaceId, unitId, {
        spaces: spacesData
      });

      setUnit(response.unit);
      setEditingSpaces(false);
    } catch (err: any) {
      console.error('Error updating spaces:', err);
      toast.error(err.response?.data?.message || 'Failed to update spaces');
    } finally {
      setSavingSpaces(false);
    }
  };

  // Allocations handlers
  const handleEditAllocations = () => {
    if (!unit) return;
    setAllocationsData({
      eligibleToBuy: {
        parking: unit.eligibleToBuy?.parking || false,
        maxParking: unit.eligibleToBuy?.maxParking || 0,
        locker: unit.eligibleToBuy?.locker || false,
        maxLocker: unit.eligibleToBuy?.maxLocker || 0,
        bicycle: unit.eligibleToBuy?.bicycle || false,
        maxBicycle: unit.eligibleToBuy?.maxBicycle || 0
      }
    });
    setEditingAllocations(true);
  };

  const handleCancelAllocations = () => {
    setEditingAllocations(false);
  };

  const handleEligibleToBuyChange = (field: 'parking' | 'locker' | 'bicycle', checked: boolean) => {
    setAllocationsData(prev => ({
      ...prev,
      eligibleToBuy: {
        ...prev.eligibleToBuy,
        [field]: checked,
        [`max${field.charAt(0).toUpperCase() + field.slice(1)}`]: checked ? prev.eligibleToBuy[`max${field.charAt(0).toUpperCase() + field.slice(1)}` as keyof typeof prev.eligibleToBuy] : 0
      }
    }));
  };

  const handleMaxCountChange = (field: 'maxParking' | 'maxLocker' | 'maxBicycle', value: number) => {
    setAllocationsData(prev => ({
      ...prev,
      eligibleToBuy: {
        ...prev.eligibleToBuy,
        [field]: value
      }
    }));
  };

  const handleSaveAllocations = async () => {
    if (!unit) return;

    try {
      setSavingAllocations(true);
      const response = await unitService.update(workspaceId, unitId, {
        eligibleToBuy: allocationsData.eligibleToBuy
      });

      setUnit(response.unit);
      setEditingAllocations(false);
      toast.success('Eligible to buy settings updated successfully');
    } catch (err: any) {
      console.error('Error updating eligible to buy settings:', err);
      toast.error(err.response?.data?.message || 'Failed to update eligible to buy settings');
    } finally {
      setSavingAllocations(false);
    }
  };

  // Allocation Modal handlers
  const handleOpenParkingModal = async () => {
    if (!unit || !unit.projectId) return;

    try {
      setLoadingParkingTypes(true);
      const response = await api.get(`/workspaces/${workspaceId}/parking-types`, {
        params: { projectId: unit.projectId }
      });

      const parkingTypes = response.data.parkingTypes || [];
      const existingAllocations = unit.allocateParking || [];
      const existingAllocationsMap: Record<string, any> = {};

      existingAllocations.forEach((item: any) => {
        existingAllocationsMap[item.type] = item;
      });

      const merged = parkingTypes.map((typeItem: any) => ({
        type: typeItem.name,
        number: existingAllocationsMap[typeItem.name]?.eligibleNo || 0,
        price: existingAllocationsMap[typeItem.name]?.price || typeItem.price || 0
      }));

      setParkingAllocations(merged);
      setLoadingParkingTypes(false);
      setShowParkingModal(true);
    } catch (error) {
      console.error('Error fetching parking types:', error);
      toast.error('Failed to fetch parking types');
      setLoadingParkingTypes(false);
    }
  };

  const handleOpenLockerModal = async () => {
    if (!unit || !unit.projectId) return;

    try {
      setLoadingLockerTypes(true);
      const response = await api.get(`/workspaces/${workspaceId}/locker-types`, {
        params: { projectId: unit.projectId }
      });

      const lockerTypes = response.data.lockerTypes || [];
      const existingAllocations = unit.allocateLocker || [];
      const existingAllocationsMap: Record<string, any> = {};

      existingAllocations.forEach((item: any) => {
        existingAllocationsMap[item.type] = item;
      });

      const merged = lockerTypes.map((typeItem: any) => ({
        type: typeItem.name,
        number: existingAllocationsMap[typeItem.name]?.eligibleNo || 0,
        price: existingAllocationsMap[typeItem.name]?.price || typeItem.price || 0
      }));

      setLockerAllocations(merged);
      setLoadingLockerTypes(false);
      setShowLockerModal(true);
    } catch (error) {
      console.error('Error fetching locker types:', error);
      toast.error('Failed to fetch locker types');
      setLoadingLockerTypes(false);
    }
  };

  const handleOpenBicycleModal = async () => {
    if (!unit || !unit.projectId) return;

    try {
      setLoadingBicycleTypes(true);
      const response = await api.get(`/workspaces/${workspaceId}/bicycle-types`, {
        params: { projectId: unit.projectId }
      });

      const bicycleTypes = response.data.bicycleTypes || [];
      const existingAllocations = unit.allocateBicycle || [];
      const existingAllocationsMap: Record<string, any> = {};

      existingAllocations.forEach((item: any) => {
        existingAllocationsMap[item.type] = item;
      });

      const merged = bicycleTypes.map((typeItem: any) => ({
        type: typeItem.name,
        number: existingAllocationsMap[typeItem.name]?.eligibleNo || 0,
        price: existingAllocationsMap[typeItem.name]?.price || typeItem.price || 0
      }));

      setBicycleAllocations(merged);
      setLoadingBicycleTypes(false);
      setShowBicycleModal(true);
    } catch (error) {
      console.error('Error fetching bicycle types:', error);
      toast.error('Failed to fetch bicycle types');
      setLoadingBicycleTypes(false);
    }
  };

  const handleSaveParkingAllocation = async () => {
    if (!unit) return;

    try {
      // Validate total allocation doesn't exceed max eligible
      const totalAllocated = parkingAllocations.reduce((sum, a) => sum + a.number, 0);
      const maxParking = unit.eligibleToBuy?.maxParking || 0;

      if (totalAllocated > maxParking) {
        toast.error(`Total parking allocation (${totalAllocated}) cannot exceed maximum eligible (${maxParking})`);
        return;
      }

      setSavingAllocation(true);
      const allocateData = parkingAllocations.map(a => ({
        type: a.type,
        eligibleNo: a.number,
        price: a.price
      }));
      const response = await unitService.update(workspaceId, unitId, {
        allocateParking: allocateData
      });

      setUnit(response.unit);
      setShowParkingModal(false);
      toast.success('Parking allocation updated successfully');
    } catch (error: any) {
      console.error('Error saving parking allocation:', error);
      toast.error(error.response?.data?.message || 'Failed to save parking allocation');
    } finally {
      setSavingAllocation(false);
    }
  };

  const handleSaveLockerAllocation = async () => {
    if (!unit) return;

    try {
      // Validate total allocation doesn't exceed max eligible
      const totalAllocated = lockerAllocations.reduce((sum, a) => sum + a.number, 0);
      const maxLocker = unit.eligibleToBuy?.maxLocker || 0;

      if (totalAllocated > maxLocker) {
        toast.error(`Total locker allocation (${totalAllocated}) cannot exceed maximum eligible (${maxLocker})`);
        return;
      }

      setSavingAllocation(true);
      const allocateData = lockerAllocations.map(a => ({
        type: a.type,
        eligibleNo: a.number,
        price: a.price
      }));
      const response = await unitService.update(workspaceId, unitId, {
        allocateLocker: allocateData
      });

      setUnit(response.unit);
      setShowLockerModal(false);
      toast.success('Locker allocation updated successfully');
    } catch (error: any) {
      console.error('Error saving locker allocation:', error);
      toast.error(error.response?.data?.message || 'Failed to save locker allocation');
    } finally {
      setSavingAllocation(false);
    }
  };

  const handleSaveBicycleAllocation = async () => {
    if (!unit) return;

    try {
      // Validate total allocation doesn't exceed max eligible
      const totalAllocated = bicycleAllocations.reduce((sum, a) => sum + a.number, 0);
      const maxBicycle = unit.eligibleToBuy?.maxBicycle || 0;

      if (totalAllocated > maxBicycle) {
        toast.error(`Total bicycle allocation (${totalAllocated}) cannot exceed maximum eligible (${maxBicycle})`);
        return;
      }

      setSavingAllocation(true);
      const allocateData = bicycleAllocations.map(a => ({
        type: a.type,
        eligibleNo: a.number,
        price: a.price
      }));
      const response = await unitService.update(workspaceId, unitId, {
        allocateBicycle: allocateData
      });

      setUnit(response.unit);
      setShowBicycleModal(false);
      toast.success('Bicycle allocation updated successfully');
    } catch (error: any) {
      console.error('Error saving bicycle allocation:', error);
      toast.error(error.response?.data?.message || 'Failed to save bicycle allocation');
    } finally {
      setSavingAllocation(false);
    }
  };

  // Notes handlers
  const handleAddNote = () => {
    setNoteContent('');
    setShowNoteDialog(true);
  };

  const handleSaveNote = async () => {
    if (!noteContent.trim()) {
      toast.warning('Please enter note content');
      return;
    }

    try {
      setSavingNote(true);
      const newNote = await createNote(workspaceId, 'unit', unitId, {
        content: noteContent
      });
      setNotes(prev => [newNote, ...prev]);
      setShowNoteDialog(false);
      setNoteContent('');
    } catch (err: any) {
      console.error('Error saving note:', err);
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteNote(workspaceId, noteId);
      setNotes(prev => prev.filter(n => n._id !== noteId));
      setDeleteNoteId(null);
    } catch (err: any) {
      console.error('Error deleting note:', err);
      toast.error('Failed to delete note');
    }
  };

  // Video handlers
  const handleAddVideo = async (video: Omit<Video, '_id' | 'createdAt'>) => {
    try {
      const newVideo = await videoService.createVideo(workspaceId, 'unit', unitId, {
        title: video.title,
        url: video.url,
        type: video.type,
        thumbnail: video.thumbnail
      });

      setVideos(prev => [...prev, newVideo]);
    } catch (error: any) {
      console.error('Error adding video:', error);
      throw new Error(error.response?.data?.message || 'Failed to add video');
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await videoService.deleteVideo(workspaceId, videoId);
      setVideos(prev => prev.filter(v => v._id !== videoId));
    } catch (error: any) {
      console.error('Error deleting video:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete video');
    }
  };

  const handleUpdateVideo = async (videoId: string, title: string) => {
    try {
      await videoService.updateVideo(workspaceId, videoId, { title });
      setVideos(prev => prev.map(v =>
        v._id === videoId ? { ...v, title } : v
      ));
    } catch (error: any) {
      console.error('Error updating video:', error);
      throw new Error(error.response?.data?.message || 'Failed to update video');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    // If more than 5 days, show full date and time
    if (diffInDays > 5) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // If today
    if (diffInDays === 0) {
      if (diffInHours === 0) {
        if (diffInMinutes === 0) {
          return 'Just now';
        }
        return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;
      }
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    // If within 5 days
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="model-details-slider unit-details-slider">
        <div className="model-details-loading">
          <div className="builders-loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !unit) {
    return (
      <div className="model-details-slider unit-details-slider">
        <div className="model-details-error">
          <p>{error || 'Unit not found'}</p>
          <button className="btn btn-secondary btn-md" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="model-details-slider unit-details-slider">
      {/* Header */}
      <div className="model-details-header">
        <div className="model-details-header-left">
          <button className="model-details-back-btn" onClick={onClose}>
            ← Back
          </button>
          <div className="model-details-title">
            <h2>Unit: {unit.unitNo}</h2>
            <p>Project: {unit.projectName} | Model: {unit.modelName}</p>
          </div>
        </div>
        <button className="model-details-close-btn" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="model-details-content">
        <div className="model-details-grid-wrapper">
          {/* Left Column */}
          <div className="model-details-left">
          {/* Unit Information Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Unit Information</h3>
              {!editModeUnitInfo && (
                <button
                  className="model-details-tile-edit"
                  onClick={handleUnitInfoEdit}
                  title="Edit unit information"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                >
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editModeUnitInfo ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-grid">
                    <div className="model-details-field">
                      <label>Unit Number</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unit.unitNo}
                        disabled
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Status</label>
                      {unit.status === 'AVAILABLE' ? (
                        <select
                          className="form-input"
                          value={unitInfoFormData.status || ''}
                          onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, status: e.target.value as 'AVAILABLE' | 'RESERVED' | 'SOLD' | 'UNAVAILABLE' })}
                        >
                          <option value="AVAILABLE">AVAILABLE</option>
                          <option value="RESERVED">RESERVED</option>
                          <option value="SOLD">SOLD</option>
                          <option value="UNAVAILABLE">UNAVAILABLE</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-input"
                          value={unit.status}
                          disabled
                        />
                      )}
                    </div>
                    <div className="model-details-field">
                      <label>Model</label>
                      {unit.status === 'AVAILABLE' ? (
                        <select
                          className="form-input"
                          value={unitInfoFormData.modelId || ''}
                          onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, modelId: e.target.value })}
                        >
                          {models.map((model) => (
                            <option key={model._id} value={model._id}>
                              {model.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-input"
                          value={unit.modelName || ''}
                          disabled
                        />
                      )}
                    </div>
                    <div className="model-details-field">
                      <label>Building Type</label>
                      <select
                        className="form-input"
                        value={unitInfoFormData.buildingType || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, buildingType: e.target.value as 'Condominium' | 'Townhouse' | 'Detached' | 'Semi-Detached' | undefined })}
                      >
                        <option value=""></option>
                        <option value="Condominium">Condominium</option>
                        <option value="Townhouse">Townhouse</option>
                        <option value="Detached">Detached</option>
                        <option value="Semi-Detached">Semi-Detached</option>
                      </select>
                    </div>
                    <div className="model-details-field">
                      <label>Floor (Marketing)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.floorMarketing || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, floorMarketing: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Floor (Legal)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unitInfoFormData.floorLegal || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, floorLegal: e.target.value })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Bedrooms</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.bed || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, bed: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Bathrooms</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.bath || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, bath: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Den</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.den || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, den: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Flex</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.flex || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, flex: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Media</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.media || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, media: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Ceiling (Ft.)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.ceiling || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, ceiling: e.target.value ? parseFloat(e.target.value) : undefined })}
                        step="0.1"
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Area (Sq.Ft)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.area || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, area: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Outdoor Type</label>
                      <select
                        className="form-input"
                        value={unitInfoFormData.outdoorType || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, outdoorType: e.target.value as 'Balcony' | 'Terrace' | 'Patio' | 'None' | undefined })}
                      >
                        <option value=""></option>
                        <option value="Balcony">Balcony</option>
                        <option value="Terrace">Terrace</option>
                        <option value="Patio">Patio</option>
                        <option value="None">None</option>
                      </select>
                    </div>
                    <div className="model-details-field">
                      <label>Outdoor Area (Sq.Ft)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.outdoorArea || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, outdoorArea: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Unit No Marketing</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.unitNoMarketing || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, unitNoMarketing: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Unit No Legal</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unitInfoFormData.unitNoLegal || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, unitNoLegal: e.target.value })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Unit Enrolment Number</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unitInfoFormData.unitEnrolmentNo || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, unitEnrolmentNo: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleUnitInfoCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleUnitInfoSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                <div className="model-detail-item">
                  <div className="model-detail-label">Unit Number</div>
                  <div className="model-detail-value">{unit.unitNo}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Status</div>
                  <div className="model-detail-value">
                    <span className={`status-badge badge-${unit.status.toLowerCase()}`}>
                      {unit.status}
                    </span>
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Model</div>
                  <div className="model-detail-value">{unit.modelName || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Building Type</div>
                  <div className="model-detail-value">{unit.buildingType || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Floor (Marketing)</div>
                  <div className="model-detail-value">{unit.floorMarketing || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Floor (Legal)</div>
                  <div className="model-detail-value">{unit.floorLegal || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Bedrooms</div>
                  <div className="model-detail-value">{unit.bed || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Bathrooms</div>
                  <div className="model-detail-value">{unit.bath || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Den</div>
                  <div className="model-detail-value">{unit.den || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Flex</div>
                  <div className="model-detail-value">{unit.flex || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Media</div>
                  <div className="model-detail-value">{unit.media || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Ceiling (Ft.)</div>
                  <div className="model-detail-value">{unit.ceiling || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Area (Sq.Ft)</div>
                  <div className="model-detail-value">{unit.area || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Outdoor Type</div>
                  <div className="model-detail-value">{unit.outdoorType || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Outdoor Area (Sq.Ft)</div>
                  <div className="model-detail-value">{unit.outdoorArea || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Unit No Marketing</div>
                  <div className="model-detail-value">{unit.unitNoMarketing || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Unit No Legal</div>
                  <div className="model-detail-value">{unit.unitNoLegal || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Unit Enrolment Number</div>
                  <div className="model-detail-value">{unit.unitEnrolmentNo || '-'}</div>
                </div>
              </div>
            )}
          </div>

          {/* Floor Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Floor Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="floor_layout"
              />
            </div>
          </div>

          {/* Suite Customization Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Suite Customization Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="suite_customization"
              />
            </div>
          </div>

          {/* Electrical Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Electrical Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="electrical_layout"
              />
            </div>
          </div>

          {/* Key Maps Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Key Maps</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="key_maps"
              />
            </div>
          </div>

          {/* Video Gallery Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Video Gallery</h3>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => videoGalleryRef.current?.openAddDialog()}
              >
                <LinkIcon size={16} style={{ marginRight: '6px' }} />
                Add Video
              </button>
            </div>
            <div className="model-details-tile-content">
              <VideoGallery
                ref={videoGalleryRef}
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                videos={videos}
                onAddVideo={handleAddVideo}
                onDeleteVideo={handleDeleteVideo}
                onUpdateVideo={handleUpdateVideo}
                hideHeader={true}
              />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="model-details-right">
          {/* Finance Information */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Finance</h3>
              {!editModeFinance && (
                <button
                  className="model-details-tile-edit"
                  onClick={handleFinanceEdit}
                  title="Edit finance information"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                >
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editModeFinance ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-grid">
                    <div className="model-details-field">
                      <label>Base Price</label>
                      <input
                        type="number"
                        className="form-input"
                        value={financeFormData.price || ''}
                        onChange={(e) => setFinanceFormData({ ...financeFormData, price: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Unit Premium</label>
                      <input
                        type="number"
                        className="form-input"
                        value={financeFormData.unitPremium || ''}
                        onChange={(e) => setFinanceFormData({ ...financeFormData, unitPremium: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Floor Premium</label>
                      <input
                        type="number"
                        className="form-input"
                        value={financeFormData.floorPremium || ''}
                        onChange={(e) => setFinanceFormData({ ...financeFormData, floorPremium: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Tax</label>
                      <input
                        type="number"
                        className="form-input"
                        value={financeFormData.tax || ''}
                        onChange={(e) => setFinanceFormData({ ...financeFormData, tax: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Sales Price</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unit.salesPrice ? `$${unit.salesPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                        disabled
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Price Per Unit (Sq.Ft)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unit.salesPrice && unit.area ? `$${(unit.salesPrice / unit.area).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleFinanceCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleFinanceSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                <div className="model-detail-item">
                  <div className="model-detail-label">Base Price</div>
                  <div className="model-detail-value">
                    {unit.price ? `$${unit.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Unit Premium</div>
                  <div className="model-detail-value">
                    {unit.unitPremium ? `$${unit.unitPremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Floor Premium</div>
                  <div className="model-detail-value">
                    {unit.floorPremium ? `$${unit.floorPremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Tax</div>
                  <div className="model-detail-value">
                    {unit.tax ? `$${unit.tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Sales Price</div>
                  <div className="model-detail-value">
                    {unit.salesPrice ? `$${unit.salesPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Price Per Unit (Sq.Ft)</div>
                  <div className="model-detail-value">
                    {unit.salesPrice && unit.area
                      ? `$${(unit.salesPrice / unit.area).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '-'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Deal Information */}
          {unit.dealId && (
            <div className="model-details-tile">
              <div className="model-details-tile-header">
                <h3>Deal Information</h3>
              </div>
              <div className="model-details-tile-content">
                <div className="model-detail-item">
                  <div className="model-detail-label">Deal ID</div>
                  <div className="model-detail-value">{unit.dealId}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Deal Type</div>
                  <div className="model-detail-value">{unit.dealType || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Deal Stage</div>
                  <div className="model-detail-value">{unit.dealStage || '-'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Eligible to Buy */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Eligible to Buy</h3>
              {!editingAllocations && (
                <button className="model-details-tile-edit" onClick={handleEditAllocations} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}>
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editingAllocations ? (
              <>
                <div className="model-details-tile-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Parking */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minWidth: '120px' }}>
                      <input
                        type="checkbox"
                        checked={allocationsData.eligibleToBuy.parking}
                        onChange={(e) => handleEligibleToBuyChange('parking', e.target.checked)}
                      />
                      Parking
                    </label>
                    {allocationsData.eligibleToBuy.parking && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <label htmlFor="maxParking" style={{ whiteSpace: 'nowrap', fontSize: '14px', color: '#6B7280' }}>
                          Max # of Parkings
                        </label>
                        <input
                          type="number"
                          id="maxParking"
                          className="form-input"
                          value={allocationsData.eligibleToBuy.maxParking}
                          onChange={(e) => handleMaxCountChange('maxParking', parseInt(e.target.value) || 0)}
                          min="0"
                          style={{ maxWidth: '150px' }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Locker */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minWidth: '120px' }}>
                      <input
                        type="checkbox"
                        checked={allocationsData.eligibleToBuy.locker}
                        onChange={(e) => handleEligibleToBuyChange('locker', e.target.checked)}
                      />
                      Locker
                    </label>
                    {allocationsData.eligibleToBuy.locker && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <label htmlFor="maxLocker" style={{ whiteSpace: 'nowrap', fontSize: '14px', color: '#6B7280' }}>
                          Max # of Lockers
                        </label>
                        <input
                          type="number"
                          id="maxLocker"
                          className="form-input"
                          value={allocationsData.eligibleToBuy.maxLocker}
                          onChange={(e) => handleMaxCountChange('maxLocker', parseInt(e.target.value) || 0)}
                          min="0"
                          style={{ maxWidth: '150px' }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Bicycle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minWidth: '120px' }}>
                      <input
                        type="checkbox"
                        checked={allocationsData.eligibleToBuy.bicycle}
                        onChange={(e) => handleEligibleToBuyChange('bicycle', e.target.checked)}
                      />
                      Bicycle
                    </label>
                    {allocationsData.eligibleToBuy.bicycle && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <label htmlFor="maxBicycle" style={{ whiteSpace: 'nowrap', fontSize: '14px', color: '#6B7280' }}>
                          Max # of Bicycles
                        </label>
                        <input
                          type="number"
                          id="maxBicycle"
                          className="form-input"
                          value={allocationsData.eligibleToBuy.maxBicycle}
                          onChange={(e) => handleMaxCountChange('maxBicycle', parseInt(e.target.value) || 0)}
                          min="0"
                          style={{ maxWidth: '150px' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleCancelAllocations}
                    disabled={savingAllocations}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleSaveAllocations}
                    disabled={savingAllocations}
                  >
                    {savingAllocations ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Parking */}
                <div className="model-detail-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="model-detail-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={unit.eligibleToBuy?.parking || false}
                      disabled
                      style={{ cursor: 'default' }}
                    />
                    Parking
                  </div>
                  <div className="model-detail-value">
                    {unit.eligibleToBuy?.parking ? `Max: ${unit.eligibleToBuy?.maxParking || 0}` : '-'}
                  </div>
                </div>

                {/* Locker */}
                <div className="model-detail-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="model-detail-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={unit.eligibleToBuy?.locker || false}
                      disabled
                      style={{ cursor: 'default' }}
                    />
                    Locker
                  </div>
                  <div className="model-detail-value">
                    {unit.eligibleToBuy?.locker ? `Max: ${unit.eligibleToBuy?.maxLocker || 0}` : '-'}
                  </div>
                </div>

                {/* Bicycle */}
                <div className="model-detail-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="model-detail-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={unit.eligibleToBuy?.bicycle || false}
                      disabled
                      style={{ cursor: 'default' }}
                    />
                    Bicycle
                  </div>
                  <div className="model-detail-value">
                    {unit.eligibleToBuy?.bicycle ? `Max: ${unit.eligibleToBuy?.maxBicycle || 0}` : '-'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Allocations */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Allocations</h3>
            </div>
            <div className="model-details-tile-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Parking Section */}
              {unit.eligibleToBuy?.parking && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Parking</h4>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleOpenParkingModal}
                      disabled={loadingParkingTypes}
                    >
                      {loadingParkingTypes ? 'Loading...' : 'Allocate'}
                    </button>
                  </div>
                  {unit.allocateParking && unit.allocateParking.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {unit.allocateParking.map((item: any, index: number) => (
                        <div key={index} style={{ padding: '12px', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '6px' }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{item.type}</div>
                          <div style={{ fontSize: '14px', color: '#6B7280' }}>Quantity: {item.eligibleNo}</div>
                          <div style={{ fontSize: '14px', color: '#6B7280' }}>Price: ${item.price.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '14px', color: '#9CA3AF' }}>No parking allocated</div>
                  )}
                </div>
              )}

              {/* Locker Section */}
              {unit.eligibleToBuy?.locker && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Locker</h4>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleOpenLockerModal}
                      disabled={loadingLockerTypes}
                    >
                      {loadingLockerTypes ? 'Loading...' : 'Allocate'}
                    </button>
                  </div>
                  {unit.allocateLocker && unit.allocateLocker.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {unit.allocateLocker.map((item: any, index: number) => (
                        <div key={index} style={{ padding: '12px', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '6px' }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{item.type}</div>
                          <div style={{ fontSize: '14px', color: '#6B7280' }}>Quantity: {item.eligibleNo}</div>
                          <div style={{ fontSize: '14px', color: '#6B7280' }}>Price: ${item.price.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '14px', color: '#9CA3AF' }}>No lockers allocated</div>
                  )}
                </div>
              )}

              {/* Bicycle Section */}
              {unit.eligibleToBuy?.bicycle && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Bicycle</h4>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleOpenBicycleModal}
                      disabled={loadingBicycleTypes}
                    >
                      {loadingBicycleTypes ? 'Loading...' : 'Allocate'}
                    </button>
                  </div>
                  {unit.allocateBicycle && unit.allocateBicycle.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {unit.allocateBicycle.map((item: any, index: number) => (
                        <div key={index} style={{ padding: '12px', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '6px' }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{item.type}</div>
                          <div style={{ fontSize: '14px', color: '#6B7280' }}>Quantity: {item.eligibleNo}</div>
                          <div style={{ fontSize: '14px', color: '#6B7280' }}>Price: ${item.price.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '14px', color: '#9CA3AF' }}>No bicycles allocated</div>
                  )}
                </div>
              )}

              {/* Show message if nothing is eligible */}
              {!unit.eligibleToBuy?.parking && !unit.eligibleToBuy?.locker && !unit.eligibleToBuy?.bicycle && (
                <div style={{ fontSize: '14px', color: '#9CA3AF', textAlign: 'center', padding: '20px' }}>
                  No items are eligible to buy. Please edit to configure eligibility.
                </div>
              )}
            </div>
          </div>

          {/* Views and Spaces Row */}
          <div className="model-details-row">
            {/* Views Tile */}
            <div className="model-details-tile model-details-tile-25">
            <div className="model-details-tile-header">
              <h3>Views</h3>
              {!editingViews && (
                <button className="model-details-tile-edit" onClick={handleEditViews} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}>
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editingViews ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-sortable-container">
                    <SortableList
                      items={viewsData}
                      onChange={handleViewsChange}
                      placeholder="Add custom view name"
                      addButtonText="+"
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowAddViewsModal(true)}
                      style={{ marginTop: '12px' }}
                    >
                      + Add from List
                    </button>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleCancelViews}
                    disabled={savingViews}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleSaveViews}
                    disabled={savingViews}
                  >
                    {savingViews ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                {unit.views && unit.views.length > 0 ? (
                  <div className="model-details-list">
                    {unit.views.map((view, index) => (
                      <div key={index} className="model-details-list-item">
                        {view}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="model-details-empty">
                    <p>No views found</p>
                  </div>
                )}
              </div>
            )}
            </div>

            {/* Spaces Tile */}
            <div className="model-details-tile model-details-tile-25">
            <div className="model-details-tile-header">
              <h3>Spaces</h3>
              {!editingSpaces && (
                <button className="model-details-tile-edit" onClick={handleEditSpaces} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}>
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editingSpaces ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-sortable-container">
                    <SortableList
                      items={spacesData}
                      onChange={handleSpacesChange}
                      placeholder="Add custom space name"
                      addButtonText="+"
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowAddSpacesModal(true)}
                      style={{ marginTop: '12px' }}
                    >
                      + Add from List
                    </button>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleCancelSpaces}
                    disabled={savingSpaces}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleSaveSpaces}
                    disabled={savingSpaces}
                  >
                    {savingSpaces ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                {unit.spaces && unit.spaces.length > 0 ? (
                  <div className="model-details-list">
                    {unit.spaces.map((space, index) => (
                      <div key={index} className="model-details-list-item">
                        {space}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="model-details-empty">
                    <p>No spaces found</p>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>

          {/* Notes Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Notes</h3>
              <button className="btn btn-primary btn-sm" onClick={handleAddNote}>Add</button>
            </div>
            <div className="model-details-tile-content">
              {loadingNotes ? (
                <div className="model-details-loading">Loading notes...</div>
              ) : notes.length > 0 ? (
                <div className="model-details-notes-list">
                  {notes.map(note => (
                    <div key={note._id} className="model-details-note-item">
                      <div className="model-details-note-header">
                        <div className="model-details-note-meta">
                          <span className="model-details-note-author">
                            By {note.createdByName || note.createdBy}
                          </span>
                          <span className="model-details-note-separator">•</span>
                          <span className="model-details-note-date">
                            {formatDate(note.createdAt)}
                          </span>
                        </div>
                        <button
                          className="model-details-note-delete"
                          onClick={() => setDeleteNoteId(note._id)}
                          title="Delete note"
                        >
                          ✕
                        </button>
                      </div>
                      <div
                        className="model-details-note-content"
                        dangerouslySetInnerHTML={{ __html: note.content }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="model-details-empty">
                  <p>No notes found</p>
                </div>
              )}
            </div>
          </div>

          {/* Documents Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Documents</h3>
            </div>
            <div className="model-details-tile-content">
              <DocumentGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                fileType="document"
                acceptTypes=".pdf,.doc,.docx"
                helpText="All unit related documents can be uploaded here. PDF and Word files are accepted."
              />
            </div>
          </div>

          {/* Photo Gallery Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Photo Gallery</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="photo_gallery"
              />
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer */}
      <div className="model-details-footer">
        <button
          className="btn btn-danger btn-sm"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete Unit
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={onClose}
        >
          Close
        </button>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="model-details-modal-overlay">
          <div className="model-details-modal">
            <h3>Delete Unit</h3>
            <p>
              Are you sure you want to delete unit <strong>{unit.unitNo}</strong>? This action cannot be undone.
            </p>
            <div className="model-details-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-md"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Views Modal */}
      <AddItemsModal
        isOpen={showAddViewsModal}
        onClose={() => setShowAddViewsModal(false)}
        onAdd={handleAddViewsFromModal}
        availableOptions={modelViews.filter(option => !viewsData.includes(option))}
        title="Add Views"
        customInputPlaceholder="Enter custom view name..."
        emptyMessage={modelViews.length > 0 ? "All views from this model are already added." : "No views configured for this model."}
      />

      {/* Add Spaces Modal */}
      <AddItemsModal
        isOpen={showAddSpacesModal}
        onClose={() => setShowAddSpacesModal(false)}
        onAdd={handleAddSpacesFromModal}
        availableOptions={modelSpaces.filter(option => !spacesData.includes(option))}
        title="Add Spaces"
        customInputPlaceholder="Enter custom space name..."
        emptyMessage={modelSpaces.length > 0 ? "All spaces from this model are already added." : "No spaces configured for this model."}
      />

      {/* Add Note Dialog */}
      {showNoteDialog && (
        <div className="model-details-modal-overlay" onClick={() => setShowNoteDialog(false)}>
          <div className="model-details-modal model-details-note-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Add Note</h3>
            <RichTextEditor
              content={noteContent}
              onChange={setNoteContent}
              placeholder="Enter note content..."
            />
            <div className="model-details-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowNoteDialog(false)}
                disabled={savingNote}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleSaveNote}
                disabled={savingNote}
              >
                {savingNote ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Note Confirmation */}
      <ConfirmDialog
        isOpen={deleteNoteId !== null}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        onConfirm={() => deleteNoteId && handleDeleteNote(deleteNoteId)}
        onClose={() => setDeleteNoteId(null)}
      />

      {/* Parking Allocation Modal */}
      {showParkingModal && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>Allocate Parking (Sell Parking)</h2>

            <div style={{
              padding: '12px',
              backgroundColor: '#E0F2FE',
              border: '1px solid #7DD3FC',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              This unit is eligible to buy <strong>up to {unit?.eligibleToBuy?.maxParking || 0}</strong> parking spaces.
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Number</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {parkingAllocations.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '12px' }}>{item.type}</td>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="number"
                        className="form-input"
                        value={item.number}
                        onChange={(e) => {
                          const newAllocations = [...parkingAllocations];
                          newAllocations[index].number = parseInt(e.target.value) || 0;
                          setParkingAllocations(newAllocations);
                        }}
                        min="0"
                        style={{ width: '100px' }}
                      />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="number"
                        className="form-input"
                        value={item.price}
                        onChange={(e) => {
                          const newAllocations = [...parkingAllocations];
                          newAllocations[index].price = parseFloat(e.target.value) || 0;
                          setParkingAllocations(newAllocations);
                        }}
                        min="0"
                        step="0.01"
                        style={{ width: '120px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowParkingModal(false)}
                disabled={savingAllocation}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleSaveParkingAllocation}
                disabled={savingAllocation}
              >
                {savingAllocation ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Locker Allocation Modal */}
      {showLockerModal && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>Allocate Locker (Sell Locker)</h2>

            <div style={{
              padding: '12px',
              backgroundColor: '#E0F2FE',
              border: '1px solid #7DD3FC',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              This unit is eligible to buy <strong>up to {unit?.eligibleToBuy?.maxLocker || 0}</strong> lockers.
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Number</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {lockerAllocations.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '12px' }}>{item.type}</td>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="number"
                        className="form-input"
                        value={item.number}
                        onChange={(e) => {
                          const newAllocations = [...lockerAllocations];
                          newAllocations[index].number = parseInt(e.target.value) || 0;
                          setLockerAllocations(newAllocations);
                        }}
                        min="0"
                        style={{ width: '100px' }}
                      />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="number"
                        className="form-input"
                        value={item.price}
                        onChange={(e) => {
                          const newAllocations = [...lockerAllocations];
                          newAllocations[index].price = parseFloat(e.target.value) || 0;
                          setLockerAllocations(newAllocations);
                        }}
                        min="0"
                        step="0.01"
                        style={{ width: '120px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowLockerModal(false)}
                disabled={savingAllocation}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleSaveLockerAllocation}
                disabled={savingAllocation}
              >
                {savingAllocation ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bicycle Allocation Modal */}
      {showBicycleModal && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>Allocate Bicycle (Sell Bicycle)</h2>

            <div style={{
              padding: '12px',
              backgroundColor: '#E0F2FE',
              border: '1px solid #7DD3FC',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              This unit is eligible to buy <strong>up to {unit?.eligibleToBuy?.maxBicycle || 0}</strong> bicycle spaces.
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Number</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {bicycleAllocations.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '12px' }}>{item.type}</td>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="number"
                        className="form-input"
                        value={item.number}
                        onChange={(e) => {
                          const newAllocations = [...bicycleAllocations];
                          newAllocations[index].number = parseInt(e.target.value) || 0;
                          setBicycleAllocations(newAllocations);
                        }}
                        min="0"
                        style={{ width: '100px' }}
                      />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="number"
                        className="form-input"
                        value={item.price}
                        onChange={(e) => {
                          const newAllocations = [...bicycleAllocations];
                          newAllocations[index].price = parseFloat(e.target.value) || 0;
                          setBicycleAllocations(newAllocations);
                        }}
                        min="0"
                        step="0.01"
                        style={{ width: '120px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowBicycleModal(false)}
                disabled={savingAllocation}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleSaveBicycleAllocation}
                disabled={savingAllocation}
              >
                {savingAllocation ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Change History FAB Button */}
      <ChangeHistoryButton
        onClick={() => setHistoryDrawerOpen(true)}
        isOpen={historyDrawerOpen}
      />

      {/* Change History Drawer */}
      <ChangeHistoryDrawer
        isOpen={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        workspaceId={workspaceId}
        entityType="unit"
        entityId={unitId}
      />
    </div>
  );
};

export default UnitDetailsSlider;